/**
 * Map staff write/load ApiError.details.code (and HTTP fallback) to copy keys.
 * Row 409s stay inline — never a full-page error for a write conflict.
 */
import { ApiError } from '@/api/errors';

export interface StaffWriteErrorView {
  code: string | undefined;
  messageKey: string;
  fallback: string;
  detailsMessage: string | undefined;
  refreshInvites: boolean;
}

const WRITE_COPY: Record<string, { key: string; fallback: string; refreshInvites?: boolean }> = {
  ALREADY_ACTIVE_MEMBER: {
    key: 'team.errors.ALREADY_ACTIVE_MEMBER',
    fallback: 'This person is already on the team.',
  },
  INVITATION_ALREADY_PENDING: {
    key: 'team.errors.INVITATION_ALREADY_PENDING',
    fallback: 'An invitation is already pending for this email.',
  },
  INVITATION_ALREADY_ACCEPTED: {
    key: 'team.errors.INVITATION_ALREADY_ACCEPTED',
    fallback: 'This invitation was already accepted.',
    refreshInvites: true,
  },
  LAST_OWNER_PROTECTED: {
    key: 'team.errors.LAST_OWNER_PROTECTED',
    fallback: 'You can’t demote or disable the last owner — promote another owner first.',
  },
  SELF_MODIFICATION_FORBIDDEN: {
    key: 'team.errors.SELF_MODIFICATION_FORBIDDEN',
    fallback: 'You can’t change your own role or status.',
  },
  ROLE_NOT_ALLOWED: {
    key: 'team.errors.ROLE_NOT_ALLOWED',
    fallback: 'That role can’t be assigned. Choose Owner, Admin, or Staff.',
  },
  STATUS_NOT_ALLOWED: {
    key: 'team.errors.STATUS_NOT_ALLOWED',
    fallback: 'That status can’t be set.',
  },
  INVALID_REQUEST: {
    key: 'team.errors.INVALID_REQUEST',
    fallback: 'Check the email and try again.',
  },
  MEMBER_NOT_FOUND: {
    key: 'team.errors.MEMBER_NOT_FOUND',
    fallback: 'That person is no longer on this tenant.',
  },
  TENANT_NOT_FOUND: {
    key: 'team.loadError',
    fallback: 'Couldn’t load the team.',
  },
  UPSTREAM_UNAVAILABLE: {
    key: 'team.loadError',
    fallback: 'Couldn’t load the team.',
  },
  ACCESS_DENIED: {
    key: 'team.forbiddenTitle',
    fallback: 'You don’t have access',
  },
  FORBIDDEN: {
    key: 'team.forbiddenTitle',
    fallback: 'You don’t have access',
  },
};

function detailsCode(error: unknown): string | undefined {
  if (error instanceof ApiError && typeof error.details?.['code'] === 'string') {
    return error.details['code'];
  }
  return undefined;
}

function detailsMessage(error: unknown): string | undefined {
  if (error instanceof ApiError && typeof error.details?.['message'] === 'string') {
    const message = error.details['message'].trim();
    return message.length > 0 ? message : undefined;
  }
  return undefined;
}

export function isForbiddenStaffLoad(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === 'FORBIDDEN') return true;
  const code = detailsCode(error);
  return code === 'ACCESS_DENIED' || code === 'FORBIDDEN';
}

export function staffWriteErrorView(error: unknown): StaffWriteErrorView {
  const code = detailsCode(error);
  const mapped = code !== undefined ? WRITE_COPY[code] : undefined;
  if (mapped !== undefined) {
    return {
      code,
      messageKey: mapped.key,
      fallback: mapped.fallback,
      detailsMessage: undefined,
      refreshInvites: mapped.refreshInvites === true,
    };
  }

  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403 || (error instanceof ApiError && error.code === 'FORBIDDEN')) {
    return {
      code: code ?? 'ACCESS_DENIED',
      messageKey: 'team.forbiddenTitle',
      fallback: 'You don’t have access',
      detailsMessage: undefined,
      refreshInvites: false,
    };
  }
  if (status === 404) {
    return {
      code: code ?? 'MEMBER_NOT_FOUND',
      messageKey: 'team.errors.MEMBER_NOT_FOUND',
      fallback: 'That person is no longer on this tenant.',
      detailsMessage: undefined,
      refreshInvites: false,
    };
  }
  if (status === 400) {
    return {
      code: code ?? 'INVALID_REQUEST',
      messageKey: 'team.errors.INVALID_REQUEST',
      fallback: 'Check the email and try again.',
      detailsMessage: undefined,
      refreshInvites: false,
    };
  }
  if (status === 503) {
    return {
      code: code ?? 'UPSTREAM_UNAVAILABLE',
      messageKey: 'team.loadError',
      fallback: 'Couldn’t load the team.',
      detailsMessage: undefined,
      refreshInvites: false,
    };
  }

  const serverMessage = detailsMessage(error);
  return {
    code,
    messageKey: 'team.errors.generic',
    fallback: 'Couldn’t save that change.',
    detailsMessage: serverMessage,
    refreshInvites: false,
  };
}

export function formatStaffWriteError(
  error: unknown,
  t: (key: string, fallback?: string) => string,
): { message: string; refreshInvites: boolean; code: string | undefined } {
  const view = staffWriteErrorView(error);
  const message = view.detailsMessage ?? t(view.messageKey, view.fallback);
  return { message, refreshInvites: view.refreshInvites, code: view.code };
}
